<template>
    <k-page>
        <template v-slot:header>
            <profile-header />
        </template>
        <k-container size="lg" class="pb-20 mt-10">
            <k-panel class="mb-10">
                <div class="flex flex-row items-center">
                    <div v-if="borrower.companyLogo" class="mr-2">
                        <img class="object-scale-down" style="height: 68px;"
                             :src="`/api/guest/download/${borrower.companyLogo}`">
                    </div>
                    <div class="flex">
                        <div class="font-semibold lg:text-xl sm:text-md">{{borrower.companyName}}</div>
                    </div>
                </div>
            </k-panel>


            <div class="grid md:grid-cols-1 lg:grid-cols-3 gap-4">
                <k-panel class="md:col-span-1 lg:col-span-2">
                    <k-panel-header>
                        <div>{{ $t("about") }}</div>
                    </k-panel-header>

                    <div class="pb-5">{{borrower.description}}</div>

                    <div class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 pt-5">
                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("dateOfCreation")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    {{borrower.foundedYear}}
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("employees")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    {{borrower.numberOfEmployees}}
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("country")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    <k-country-flags slot="right"
                                                     :countries="borrower.countries"></k-country-flags>
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("kildeRating")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    {{borrower.creditRating}}
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("loanType")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right">
                                    <div v-for="product in borrower.productTypes" class="text-right text-xs"
                                         v-bind:key="product">
                                        {{product}}
                                    </div>
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("website")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    <a :href="'https://' + borrower.website" target="_blank">{{borrower.website}}</a>
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("loanOriginated")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    {{borrower.loansOriginated |
                                    numeralFormat('$0,0.00')}}
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("loansPortfolio")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    {{borrower.loansPortfolio |
                                    numeralFormat('$0,0.00')}}
                                </div>
                            </k-panel-row>
                        </k-panel>

                        <k-panel class="bg-gray-200 pb-0">
                            <k-panel-row>
                                <div slot="left" class="font-semibold">{{ $t("loansOnKilde")}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right" class="text-primary-500 font-semibold">
                                    {{borrower.borrowingOnPlatform
                                    | numeralFormat('$0,0.00')}}
                                </div>
                            </k-panel-row>
                        </k-panel>
                    </div>
                </k-panel>
                <k-panel class="col-span-1">
                    <k-panel-header>{{ $t("attachedDocuments") }}</k-panel-header>
                    <k-panel-row>
                        <div slot="left">{{ $t("presentation") }}</div>
                        <div slot="right" v-if="borrower.presentationFileReference" class="text-primary-500">
                            <a :href="`/api/guest/download/${borrower.presentationFileReference}`" target="_blank"
                               class="block">
                                {{ $t("view") }}
                            </a>
                        </div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("financialReports") }}</div>
                        <div slot="right" v-if="borrower.financeFileReference" class="text-primary-500">
                            <a :href="`/api/guest/download/${borrower.financeFileReference}`" target="_blank"
                               class="block">
                                {{ $t("view") }}
                            </a>
                        </div>
                    </k-panel-row>
                </k-panel>
            </div>


            <div v-if="tranches">
                <div class="mt-8">
                    <div class="font-semibold mb-2">{{ $t("fundsRequested") }}</div>
                    <div class="lg:hidden grid md:grid-cols-2 gap-3">
                        <k-panel v-for="item in tranches" :key="item.uuid" class="bg-white px-2 py-2 border">
                            <k-panel-row>
                                <div slot="left">{{ $t("trancheNo") }}</div>
                                <div slot="right">{{item.trancheNumber}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="left">{{ $t("maturityDate")}}</div>
                                <div slot="right">{{item.maturityDate}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="left">{{ $t("interestRate") }}</div>
                                <div slot="right">{{item.interestRate | numeralFormat('0,0.00')}}%</div>
                            </k-panel-row>

                            <k-panel-row>
                                <div slot="left">{{ $t("availableToInvest")}}</div>
                                <div slot="right">
                                    <div v-if="item.principalAvailable !== 0">
                                        {{item.principalAvailable | numeralFormat('$0,0.00')}}
                                    </div>
                                    <div v-if="item.principalAvailable === 0" style="text-align: center">
                                        <div class="font-semibold text-md">{{ $t("paid")}}</div>
                                    </div>
                                </div>
                            </k-panel-row>

                            <k-panel-row>
                                <div slot="left">{{ $t("yourInvestments") }}</div>
                                <div slot="right">{{item.investedAmount | numeralFormat('$0,0.00')}}</div>
                            </k-panel-row>
                            <k-panel-row>
                                <div slot="right">
                                    <router-link :to="`/tranche-invest/${item.uuid}`">
                                        <a-button type="primary" ghost>
                                            {{ $t("view") }}
                                        </a-button>
                                    </router-link>
                                </div>
                            </k-panel-row>
                        </k-panel>
                    </div>
                    <div class="hidden lg:block">
                        <table class="table-auto bg-white w-full">
                            <thead>
                            <tr class="text-sm bg-gray-100">
                                <th class="border px-4 py-2 text-center">
                                    {{ $t("trancheNo") }}
                                </th>
                                <th class="border px-2 py-2 text-center">{{ $t("maturityDate")}}</th>
                                <th class="border px-2 py-2 text-center">{{ $t("interestRate")}}</th>
                                <th class="border px-2 py-2 text-center">{{ $t("amountAvailable")}}</th>
                                <th class="border px-2 py-2 text-center">{{ $t("yourInvestments")}}</th>
                                <th class="border px-2 py-2 text-center"></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="item in tranches" :key="item.uuid" class="hover:bg-gray-100">
                                <td class="border px-2 py-4  whitespace-no-wrap text-center">
                                    {{item.trancheNumber}}
                                </td>

                                <td class="border px-2 py-4  whitespace-no-wrap text-center">
                                    {{item.maturityDate}}
                                </td>

                                <td class="border px-2 py-4 whitespace-no-wrap text-right">
                                    {{item.interestRate | numeralFormat('0,0.00')}}
                                </td>

                                <td class="border px-2 py-4 whitespace-no-wrap text-right">
                                    <div v-if="item.principalAvailable !== 0">
                                        {{item.principalAvailable | numeralFormat('$0,0.00')}}
                                    </div>
                                    <div v-if="item.principalAvailable === 0" style="text-align: center">
                                        <div class="font-semibold text-md">{{ $t("paid") }}</div>
                                    </div>
                                </td>

                                <td class="border px-2 py-4 whitespace-no-wrap text-right">
                                    {{item.investedAmount | numeralFormat('$0,0.00')}}
                                </td>

                                <td class="border px-1 py-4 text-center">
                                    <router-link :to="`/tranche-invest/${item.uuid}`">
                                        <a-button type="primary" ghost>
                                            {{ $t("view")}}
                                        </a-button>
                                    </router-link>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </k-container>
    </k-page>
</template>

<script>
import ProfileHeader from '@investor/components/profile/ProfileHeader';

export default {
        components: {
            ProfileHeader
        },
        data() {
            return {
                borrowerUuid: null,
                borrower: {},
                tranches: {},
                loading: false,
            }
        },
        created() {
            this.borrowerUuid = this.$route.params.borrowerUuid;
        },
        async mounted() {
            await this.refresh();
        },
        methods: {
            async refresh() {
                this.loading = true;
                try {
                    const resp = await this.$post('borrower', { borrowerUuid: this.borrowerUuid });
                    this.borrower = resp.borrower
                    this.tranches = resp.tranches
                } finally {
                    this.loading = false;
                }
            },
            async unsubscribe(trancheUuid) {
                await this.$post('tranche/unsubscribe', { trancheUuid });
                await this.refresh();
                await this.$fetchInvestorProfile();
            }
        }
    }
</script>



